import { http, URL } from './http.service';

// Get session ID from localStorage or create new one
const getSessionId = () => {
  try {
    let sessionId = localStorage.getItem('guest_session_id');
    if (!sessionId) {
      sessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('guest_session_id', sessionId);
    }
    return sessionId;
  } catch (error) {
    console.error('Error getting session ID:', error);
    return `guest_${Date.now()}_fallback`;
  }
};

// Helper function to normalize wishlist response
const normalizeWishlistResponse = (response) => {
  try {
    if (!response) {
      return { success: false, data: { items: [] } };
    }

    if (response && response.success) {
      if (response.data && response.data.items) {
        return {
          success: true,
          data: {
            items: Array.isArray(response.data.items) ? response.data.items : []
          }
        };
      } else if (response.data && response.data.data && response.data.data.items) {
        return {
          success: true,
          data: {
            items: Array.isArray(response.data.data.items) ? response.data.data.items : []
          }
        };
      }
    }
    
    if (response && response.data) {
      if (response.data.success) {
        if (response.data.data && response.data.data.items) {
          return {
            success: true,
            data: {
              items: Array.isArray(response.data.data.items) ? response.data.data.items : []
            }
          };
        } else if (response.data.data && response.data.data.cart && response.data.data.cart.items) {
          return {
            success: true,
            data: {
              items: Array.isArray(response.data.data.cart.items) ? response.data.data.cart.items : []
            }
          };
        }
      }
    }
    
    return {
      success: false,
      data: { items: [] }
    };
  } catch (error) {
    console.error('Error normalizing wishlist response:', error);
    return { success: false, data: { items: [] } };
  }
};

// Get wishlist for logged-in user
export const getUserWishlist = async (businessUuid, customerId, signal = null) => {
  try {
    if (!businessUuid || !customerId) {
      console.error('Missing required params:', { businessUuid, customerId });
      return { success: false, data: { items: [] } };
    }

    console.log('Fetching wishlist for user:', customerId);
    
    const response = await http.get(`${URL}/wishlist/${businessUuid}/user/${customerId}`, {
      signal
    });
    
    const normalized = normalizeWishlistResponse(response);
    
    return normalized;
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      console.log('Wishlist request was cancelled');
      return { 
        success: false, 
        data: { items: [] },
        error: 'canceled'
      };
    }
    
    console.error('Error fetching user wishlist:', error);
    return { 
      success: false, 
      data: { items: [] },
      error: error.message || 'Failed to fetch wishlist'
    };
  }
};

// Get wishlist for guest user
export const getGuestWishlist = async (businessUuid, signal = null) => {
  try {
    if (!businessUuid) {
      console.error('Missing businessUuid');
      return { success: false, data: { items: [] } };
    }
    
    const sessionId = getSessionId();
    console.log('Fetching wishlist for guest:', sessionId);
    
    const response = await http.get(`${URL}/wishlist/${businessUuid}/guest/${sessionId}`, {
      signal
    });
    
    return normalizeWishlistResponse(response);
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      console.log('Wishlist request was cancelled');
      return { 
        success: false, 
        data: { items: [] },
        error: 'canceled'
      };
    }
    
    console.error('Error fetching guest wishlist:', error);
    return { success: false, data: { items: [] } };
  }
};

// Check if item is in wishlist for logged-in user
export const checkUserWishlistItem = async (businessUuid, customerId, productId, variantId = null, signal = null) => {
  try {
    if (!businessUuid || !customerId || !productId) {
      console.error('Missing required params:', { businessUuid, customerId, productId });
      return { success: false, exists: false };
    }
    
    const variantParam = variantId ? variantId : 'null';
    
    const response = await http.get(`${URL}/wishlist/${businessUuid}/user/${customerId}/check/${productId}/${variantParam}`, {
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        exists: response.data.data?.exists || false
      };
    }
    
    return { success: false, exists: false };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, exists: false };
    }
    console.error('Error checking wishlist item:', error);
    return { success: false, exists: false };
  }
};

// Check if item is in wishlist for guest user
export const checkGuestWishlistItem = async (businessUuid, productId, variantId = null, signal = null) => {
  try {
    if (!businessUuid || !productId) {
      console.error('Missing required params:', { businessUuid, productId });
      return { success: false, exists: false };
    }
    
    const sessionId = getSessionId();
    const variantParam = variantId ? variantId : 'null';
    
    const response = await http.get(`${URL}/wishlist/${businessUuid}/guest/${sessionId}/check/${productId}/${variantParam}`, {
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        exists: response.data.data?.exists || false
      };
    }
    
    return { success: false, exists: false };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, exists: false };
    }
    console.error('Error checking wishlist item:', error);
    return { success: false, exists: false };
  }
};

// Toggle wishlist item for logged-in user
export const toggleUserWishlistItem = async (businessUuid, customerId, itemData, signal = null) => {
  try {
    if (!businessUuid || !customerId || !itemData) {
      console.error('Missing required params:', { businessUuid, customerId, itemData });
      return { success: false, message: 'Missing required parameters' };
    }
    
    const response = await http.post(`${URL}/wishlist/${businessUuid}/user/${customerId}/toggle`, itemData, {
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.data?.message || 'Wishlist updated'
      };
    }
    
    return { success: false, message: 'Failed to update wishlist' };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, message: 'Request cancelled' };
    }
    
    console.error('Error toggling wishlist item:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to update wishlist' 
    };
  }
};

// Toggle wishlist item for guest user
export const toggleGuestWishlistItem = async (businessUuid, itemData, signal = null) => {
  try {
    if (!businessUuid || !itemData) {
      console.error('Missing required params:', { businessUuid, itemData });
      return { success: false, message: 'Missing required parameters' };
    }
    
    const sessionId = getSessionId();
    
    const response = await http.post(`${URL}/wishlist/${businessUuid}/guest/${sessionId}/toggle`, itemData, {
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.data?.message || 'Wishlist updated'
      };
    }
    
    return { success: false, message: 'Failed to update wishlist' };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, message: 'Request cancelled' };
    }
    
    console.error('Error toggling wishlist item:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to update wishlist' 
    };
  }
};

// Remove from wishlist for logged-in user
export const removeFromUserWishlist = async (businessUuid, customerId, productId, variantId = null, signal = null) => {
  try {
    if (!businessUuid || !customerId || !productId) {
      console.error('Missing required params:', { businessUuid, customerId, productId });
      return { success: false, message: 'Missing required parameters' };
    }
    
    const response = await http.delete(`${URL}/wishlist/${businessUuid}/user/${customerId}/remove`, {
      data: { productId, variantId },
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: 'Item removed from wishlist'
      };
    }
    
    return { success: false, message: 'Failed to remove item' };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, message: 'Request cancelled' };
    }
    
    console.error('Error removing from wishlist:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to remove item' 
    };
  }
};

// Remove from wishlist for guest user
export const removeFromGuestWishlist = async (businessUuid, productId, variantId = null, signal = null) => {
  try {
    if (!businessUuid || !productId) {
      console.error('Missing required params:', { businessUuid, productId });
      return { success: false, message: 'Missing required parameters' };
    }
    
    const sessionId = getSessionId();
    
    const response = await http.delete(`${URL}/wishlist/${businessUuid}/guest/${sessionId}/remove`, {
      data: { productId, variantId },
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: 'Item removed from wishlist'
      };
    }
    
    return { success: false, message: 'Failed to remove item' };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, message: 'Request cancelled' };
    }
    
    console.error('Error removing from wishlist:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to remove item' 
    };
  }
};

// Clear user wishlist
export const clearUserWishlist = async (businessUuid, customerId, signal = null) => {
  try {
    if (!businessUuid || !customerId) {
      console.error('Missing required params:', { businessUuid, customerId });
      return { success: false, message: 'Missing required parameters' };
    }
    
    const response = await http.delete(`${URL}/wishlist/${businessUuid}/user/${customerId}/clear`, {
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: 'Wishlist cleared successfully'
      };
    }
    
    return { success: false, message: 'Failed to clear wishlist' };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, message: 'Request cancelled' };
    }
    
    console.error('Error clearing wishlist:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to clear wishlist' 
    };
  }
};

// Clear guest wishlist
export const clearGuestWishlist = async (businessUuid, signal = null) => {
  try {
    if (!businessUuid) {
      console.error('Missing businessUuid');
      return { success: false, message: 'Missing business UUID' };
    }
    
    const sessionId = getSessionId();
    
    const response = await http.delete(`${URL}/wishlist/${businessUuid}/guest/${sessionId}/clear`, {
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: 'Wishlist cleared successfully'
      };
    }
    
    return { success: false, message: 'Failed to clear wishlist' };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, message: 'Request cancelled' };
    }
    
    console.error('Error clearing wishlist:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to clear wishlist' 
    };
  }
};

// Merge wishlists after login
export const mergeWishlists = async (businessUuid, customerId, signal = null) => {
  try {
    if (!businessUuid || !customerId) {
      console.error('Missing required params:', { businessUuid, customerId });
      return { success: false, message: 'Missing required parameters' };
    }
    
    const sessionId = getSessionId();
    
    const response = await http.post(`${URL}/wishlist/${businessUuid}/merge`, {
      sessionId,
      customerId
    }, {
      signal
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Wishlists merged successfully'
      };
    }
    
    return { success: false, message: 'Failed to merge wishlists' };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return { success: false, message: 'Request cancelled' };
    }
    
    console.error('Error merging wishlists:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to merge wishlists' 
    };
  }
};